import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { 
  useNavigate,
  Link as RouterLink
} from 'react-router-dom'

import { 
  LockOutlined as LockOutlinedIcon 
} from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Link,
  TextField,
  Typography,
} from '@mui/material'

import ReCAPTCHA from 'react-google-recaptcha'

import {
  setFlashNotice,
  clearFlashNotice,
  setFlashAlert,
  clearFlashAlert
} from '../ui/uiSlice'
import { selectAuthToken, setAuthToken } from './userSlice'
import { formatDeviseErrors } from '../../../components/devise/deviseErrors'
import { deviseFetch, showFailedRequest } from '../../../components/devise/deviseRequest'
import useSubmitting from '../../../hooks/useSubmitting'

const classes = {
  link: {
    margin: '0 5px 0 5px',
    cursor: 'pointer',
  }
}

export default function SignUp() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const storedAuthToken = useSelector(selectAuthToken)
  const csrfToken = storedAuthToken 
    || document.querySelector('meta[name=csrf-token]')?.content || ''

  const [recaptchaResponse, setRecaptchaResponse] = useState('')
  const [errors, setErrors] = useState({})
  const recaptchaRef = useRef(null)
  const [submitting, submit] = useSubmitting()

  useEffect(() => {
    if (!storedAuthToken) {
      const documentAuthToken = document.querySelector('meta[name=csrf-token]')?.content
      if (documentAuthToken) {
        dispatch(setAuthToken(documentAuthToken))
      }
    }
  }, [dispatch, storedAuthToken])

  const handleSubmit = (evt) => {
    // Prevent the default form submission. We will complete the post action
    // via xhr.
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)
    if (csrfToken) {
      data.set('authenticity_token', csrfToken)
    }
    for (let pair of data.entries()){
      console.log(pair[0], pair[1])
    }

    submit(async () => {
      // Post a new request to create a registration
      const result = await deviseFetch(dispatch, "/user.json", {
        method: 'POST',
        body: data,
        headers: {
          'Accept': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        }
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      console.log(response.status)

      if (response.status === 201){
        console.log('signed up')
        dispatch(setFlashNotice(
          'Signed up successfully. Check your email to confirm your account.'
        ))
        navigate('/app/confirmation_instructions')
        return
      }
      else if (response.status === 422){
        console.log('could not sign up')

        console.log(json_data)
        setErrors(json_data?.errors ?? {})

        // A reCAPTCHA token is single-use server-side -- Google invalidates
        // it once verify_recaptcha checks it, even on a 422 for an
        // unrelated reason (e.g. a password mismatch). Without resetting,
        // resubmitting replays the same, now-consumed token and fails with
        // a reCAPTCHA error even though the box is still ticked.
        recaptchaRef.current?.reset()
        setRecaptchaResponse('')

        // List every error, including ones no field shows (e.g. reCAPTCHA).
        dispatch(setFlashAlert(formatDeviseErrors(json_data)))

        return
      }

      dispatch(setFlashAlert(formatDeviseErrors(json_data)))
    })
  }

  const rc_site_key = '6LfzDkccAAAAAO1K3DqAhD8Qar5jKxndtckhuaih'

  const onRecaptchaChange = (value) => {
    setRecaptchaResponse(value)
  }

  const handleLinkClick = (e) => {
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())
  }

  return (
    <React.Fragment>
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 9,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography component='h1' variant='h5'>
            Sign up
          </Typography>
          <Box 
            component="form"
            action="/user"
            method="post"
            sx={{ mt: 1, width: '100%', maxWidth: 400 }}
            onSubmit={handleSubmit} 
            noValidate
          >
            <TextField
              margin='normal'
              required
              fullWidth
              label='Login'
              id='user[login]'
              name='user[login]'
              autoComplete='login'
              autoFocus
            />
            <TextField
              margin='normal'
              required
              fullWidth
              id='user[email]'
              label='Email Address'
              name='user[email]'
              autoComplete='email'
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="user[password]"
              label="Password"
              type="password"
              id="user[password]"
              autoComplete="password"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="user[password_confirmation]"
              label="Confirm password"
              type="password"
              id="user[password_confirmation]"
              autoComplete="confirm password"
            />
            <p>Recaptcha</p>
            {/* The reCAPTCHA widget has a fixed ~304px width that doesn't shrink,
                so it can force horizontal overflow on narrow viewports. Scaling
                it down (with transform-origin left) keeps it within the form's
                width instead of widening the page. */}
            <Box
              sx={{
                width: '100%',
                overflow: 'hidden',
                transform: { xs: 'scale(0.85)', sm: 'none' },
                transformOrigin: 'left top',
              }}
            >
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={rc_site_key}
                onChange={onRecaptchaChange}
              />
            </Box>
            <input 
              type="hidden" 
              name="g-recaptcha-response" 
              value={recaptchaResponse}
            />
            <input type="hidden" name="commit" value="Sign up" />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              loading={submitting}
              sx={{ mt: 3, mb: 2 }}
            >
              Sign up
            </Button>
            <Grid container>
              <Grid sx={{ flexGrow: 1 }}>
                <Link 
                  component={RouterLink}
                  onClick={handleLinkClick}
                  to='/app/sign_in'
                  sx={classes.link}
                  rel='nofollow'
                  variant='body2'
                >
                  Already have an account? Sign In
                </Link>
              </Grid>
            </Grid>            
          </Box>
        </Box>
      </Container>
    </React.Fragment>
  )
}

